import { useState } from 'react';
import { currencySymbol } from 'src/common/currency';
import { useCurrency } from 'src/common/hooks/useCurrency';
import { NumberInputWithLabel } from 'src/design-system/NumberInputWithLabel';
import { toAmount } from './allocations';

type MoneyInputProps = {
  value: number | '';
  onChange: (value: number | '') => void;
  id?: string;
  label?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  'data-autofocus'?: boolean;
};

const hasPence = (value: number | '') => typeof value === 'number' && !Number.isInteger(value);

/**
 * Every money field in the cashflow forms. Amounts read grouped in thousands, as the tab shows them,
 * so a comma can only ever be a thousands separator: Mantine otherwise takes a typed or pasted
 * comma as the decimal point, and "2,400" would become 2.40. Pence are shown to two places when
 * there are any, so a saved 2400.5 reads 2,400.50, while a whole number of pounds gains no ".00".
 */
export function MoneyInput({ value, onChange, ...props }: MoneyInputProps) {
  const currency = useCurrency();
  // While the field has focus it shows exactly what was typed. Redrawn from the amount instead,
  // "1,200.0" would lose its ".0" once backspacing makes the amount whole, and "1,200.5" would be
  // padded to "1,200.50" before the second digit of pence could be typed.
  const [typed, setTyped] = useState<string | number | null>(null);
  const isEditing = typed !== null;
  // The number formatter reports focus a tick late, so an edit made straight away (a clear, a
  // paste) can come first; the amount as shown only seeds the field when nothing is typed yet.
  const seedFromAmount = () =>
    setTyped(current => current ?? (hasPence(value) ? Number(value).toFixed(2) : value));
  return (
    <NumberInputWithLabel
      {...props}
      leftSection={currencySymbol(currency)}
      value={isEditing ? typed : value}
      onFocus={seedFromAmount}
      onBlur={() => setTyped(null)}
      onChange={next => {
        setTyped(next);
        onChange(toAmount(next));
      }}
      thousandSeparator=","
      allowedDecimalSeparators={['.']}
      decimalScale={2}
      fixedDecimalScale={!isEditing && hasPence(value)}
      min={0}
      placeholder="0"
      hideControls
    />
  );
}
